import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Box,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchWebsites, setSelectedWebsite } from '../../store/slices/websiteSlice';
import { Website } from '../../types';
import { getCountryFlag, cleanUrl, sortWebsitesByOrder } from '../../utils/countryFlags';

interface WebsiteSelectorProps {
  showAddButton?: boolean;
  onAddClick?: () => void;
}

const WebsiteSelector: React.FC<WebsiteSelectorProps> = ({ 
  showAddButton = true, 
  onAddClick 
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { websites, selectedWebsite, isLoading } = useSelector(
    (state: RootState) => state.website
  );
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  useEffect(() => {
    // Always try to fetch websites if we don't have any and not currently loading
    if (websites.length === 0 && !isLoading) {
      console.log('📌 WebsiteSelector: Fetching websites...');
      dispatch(fetchWebsites());
    }
  }, [dispatch, websites.length, isLoading]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const websiteId = event.target.value;
    const website = websites.find((site) => site.website_id === websiteId);
    if (website) {
      dispatch(setSelectedWebsite(website));
    }
  };

  const handleAddWebsite = () => {
    if (onAddClick) {
      onAddClick();
    }
  };

  // Temporarily removed auth check for debugging
  // if (!isAuthenticated) {
  //   return null;
  // }

  // Sort websites according to the predefined order
  const sortedWebsites = sortWebsitesByOrder(websites);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <FormControl
        sx={{ minWidth: 200, mr: showAddButton ? 1 : 0 }}
        size="small"
      >
        <InputLabel id="website-select-label">Website</InputLabel>
        <Select
          labelId="website-select-label"
          id="website-select"
          value={selectedWebsite?.website_id || ''}
          label="Website"
          onChange={handleChange}
          disabled={isLoading}
          startAdornment={
            isLoading ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : null
          }
        >
          {sortedWebsites.map((website: Website) => (
            <MenuItem key={website.website_id} value={website.website_id}>
              {getCountryFlag(website.country)} {cleanUrl(website.base_url)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showAddButton && (
        <Tooltip title="Add New Website">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleAddWebsite}
            sx={{ minWidth: 40, width: 40, height: 40, p: 0 }}
          >
            <AddIcon />
          </Button>
        </Tooltip>
      )}
    </Box>
  );
};

export default WebsiteSelector;